import Text from '$tablet-app/src/app/components/Text';
import { palette } from '$tablet-app/src/app/theme';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-native-fontawesome';
import React, { FC } from 'react';
import { StyleProp, TextStyle, ViewStyle } from 'react-native';
import { Pressable } from 'react-native-web-hover';
import styled from 'styled-components/native';
import Row from '../Row';
import Spacer from '../Spacer';

type ButtonSizes = 'small' | 'medium' | 'large' | 'xlarge';
type ButtonVariants = 'primary' | 'secondary' | 'text';

interface Props {
  size?: ButtonSizes;
  variant?: ButtonVariants;
  icon?: IconProp;
  iconColor?: string;
  iconSize?: number;
  testID?: string;
  style?: StyleProp<ViewStyle>;
  textStyle?: StyleProp<TextStyle>;
  disabled?: boolean;
  onPress?: () => void;
  children?: React.ReactNode;
}

/** We should be using this button from now on */
const NewButton: FC<Props> = (props) => {
  const {
    size = 'medium',
    variant = 'primary',
    icon,
    iconColor,
    iconSize,
    children,
    style,
    ...rest
  } = props;

  const style_override = style && 'length' in style ? style[0] : style;

  return (
    <Pressable
      style={({ hovered, pressed }) => ({
        ...button_base_styles,
        ...button_size_styles[size],
        ...button_variant_styles[variant][
          pressed ? 'pressed' : hovered ? 'hovered' : 'default'
        ],
        ...style_override,
      })}
      {...rest}
    >
      {icon ? (
        <Row>
          <ButtonIcon
            button_size={size}
            text_variant={variant}
            icon={icon}
            color={iconColor}
            size={iconSize}
          />
          {children ? <Spacer width="8px" /> : null}
        </Row>
      ) : null}

      {children ? (
        <ButtonText
          variant="caption"
          weight="semibold"
          size={size}
          text_variant={variant}
          style={props.textStyle}
        >
          {children}
        </ButtonText>
      ) : null}
    </Pressable>
  );
};

const button_base_styles = {
  alignSelf: 'flex-start',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
};

const button_size_styles = {
  small: {
    borderRadius: 12,
    padding: 9,
  },
  medium: {
    borderRadius: 15,
    padding: 12,
  },
  large: {
    borderRadius: 20,
    padding: 16,
  },
  xlarge: {
    borderRadius: 22,
    padding: 20,
  },
};

const button_variant_styles = {
  primary: {
    default: {
      backgroundColor: palette.primary_70,
    },
    pressed: {
      backgroundColor: palette.primary_50,
    },
    hovered: {
      backgroundColor: palette.primary_90,
    },
  },
  secondary: {
    default: {
      backgroundColor: 'rgba(5, 22, 51, 0.1)',
    },
    pressed: {
      backgroundColor: 'rgba(5, 22, 51, 0.15);',
    },
    hovered: {
      backgroundColor: 'rgba(5, 22, 51, 0.2);',
    },
  },
  text: {
    default: {
      backgroundColor: 'transparent',
    },
    pressed: {},
    hovered: {},
  },
};

const ButtonText = styled(Text)<{
  size?: ButtonSizes;
  text_variant?: ButtonVariants;
}>`
  ${({ size, text_variant }) => `
    ${textSizeStyles[size || 'medium']}
    ${textVariantStyles[text_variant || 'primary']}
  `}
`;

const ButtonIcon = styled(FontAwesomeIcon)<{
  button_size?: ButtonSizes;
  text_variant?: ButtonVariants;
}>`
  ${({ button_size, text_variant }) => `
    ${textSizeStyles[button_size || 'medium']}
    ${textVariantStyles[text_variant || 'primary']}
  `}
`;

const textSizeStyles = {
  small: `
    font-size: 13px;
  `,
  medium: `
    font-size: 15px;
  `,
  large: `
   font-size: 17px;
  `,
  xlarge: `
    font-size: 22px;
  `,
};

const textVariantStyles = {
  primary: `
    color: white;
  `,
  secondary: `
    color: #051633;
;
  `,
  text: `
    color: #051633;
  `,
};

export default NewButton;
